import { type UseFormReturn, useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetPartnerAdminsClientContractTerm, useGetPartnerAdminsContractPlans } from '~/hooks';
import { getClientContractTermDetailLink } from '~/utils/getLinks';
import {
  convertToFormValues,
  type EditClientContractTermFormValues,
  editClientContractTermSchema,
  generateRequestBody,
} from '../logics';
import type { EditClientContractTermPresentation } from '../presentations';
import { usePutClientContractTerm } from './usePutClientContractTerm';

type Args = {
  /** 利用企業 ID */
  clientId: number;
  /** 企業契約期間 ID */
  termId: number;
};

type PresentationProps = React.ComponentProps<typeof EditClientContractTermPresentation>;

type IUseEditClientContractTerm = (args: Args) => {
  state: {
    /** 企業契約期間単一 Resource API レスポンス */
    data: ReturnType<typeof useGetPartnerAdminsClientContractTerm>['data'];
    /** 契約プラン一覧データ */
    contractPlansData: ReturnType<typeof useGetPartnerAdminsContractPlans>['data'];
    /** フォームの状態管理 */
    methods: UseFormReturn<EditClientContractTermFormValues>;
    /** 保存中かどうか */
    isSaving: boolean;
  };
  actions: {
    /** フォーム送信ハンドラ */
    handleSubmit: PresentationProps['onSubmit'];
    /** キャンセルボタンハンドラ */
    handleCancel: PresentationProps['onCancel'];
  };
};

/**
 * 企業契約期間 編集フォームの状態管理を提供する
 *
 * - threeLetterCode を Cookie から取得し、API hook に渡す
 * - フォームの初期値は convertToFormValues で API レスポンスから変換する
 * - 保存成功時は単一 Resource API のキャッシュを再検証して成功 Toast を表示し、詳細画面へ遷移する
 * - 422 エラー時は errors の各メッセージを Toast 表示する
 */
export const useEditClientContractTerm: IUseEditClientContractTerm = ({ clientId, termId }) => {
  const threeLetterCode = getThreeLetterCode() ?? '';
  const { showToast } = useToast();
  const navigate = useNavigate();

  const { data, mutate } = useGetPartnerAdminsClientContractTerm({
    threeLetterCode,
    clientId,
    termId,
  });
  const { data: contractPlansData } = useGetPartnerAdminsContractPlans({ threeLetterCode });

  const methods = useForm<EditClientContractTermFormValues>({
    defaultValues: convertToFormValues({ data }),
    criteriaMode: 'all',
    mode: 'onBlur',
    resolver: zodResolver(editClientContractTermSchema),
  });

  const { trigger, isMutating } = usePutClientContractTerm(threeLetterCode, clientId, termId, {
    onSuccess: () => {
      mutate();
      showToast({ message: '企業契約期間を更新しました。', status: 'success' });
      navigate(getClientContractTermDetailLink({ clientId, termId }));
    },
    onError: error => {
      if (error.response?.status === 422 && Array.isArray(error.response.data?.errors)) {
        error.response.data.errors.forEach((message: string) => {
          showToast({ message, status: 'danger' });
        });
        return;
      }
      showToast({ message: '企業契約期間の更新に失敗しました。', status: 'danger' });
    },
  });

  /**
   * フォームを送信する
   */
  const handleSubmit: PresentationProps['onSubmit'] = methods.handleSubmit(formData => {
    trigger(generateRequestBody(formData));
  });

  /**
   * キャンセルして企業契約期間詳細へ遷移する
   */
  const handleCancel: PresentationProps['onCancel'] = () => {
    navigate(getClientContractTermDetailLink({ clientId, termId }));
  };

  return {
    state: { data, contractPlansData, methods, isSaving: isMutating },
    actions: { handleSubmit, handleCancel },
  };
};
